import {
  Badge,
  Body1Strong,
  Button,
  Caption1,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  Option,
  Spinner,
  Subtitle1,
  Textarea,
  tokens,
} from "@fluentui/react-components";
import { CameraRegular, DismissRegular, ImageAddRegular } from "@fluentui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { catalogue } from "../api/catalogue";
import { ApiError } from "../api/client";
import { commerce, type ReceivedUnit } from "../api/commerce";
import { LabelSheet } from "../stock/LabelSheet";
import { baeTypography } from "../theme/baeTheme";
import { PHONE } from "../ui/tokens";
import { AttributeField, type AttributeValue } from "./AttributeField";
import {
  emptyVariantChoice,
  MAX_VARIANT_AXES,
  useVariantPlan,
  type VariantChoice,
  VariantPicker,
  variantQuantitiesFrom,
} from "./VariantPicker";

/**
 * How many photos one piece may carry.
 *
 * Mirrors the API, which is the authority and refuses a ninth. More than this is a folder rather
 * than a gallery, and every one is storage a small blob account pays for (ADR-0017).
 */
const MAX_PHOTOS = 8;

const useStyles = makeStyles({
  // One column, comfortable targets. This is used one-handed on a phone in a shop, so the layout
  // stays single-column at every width rather than becoming a desktop form on a wide screen.
  page: {
    display: "grid",
    gap: tokens.spacingVerticalL,
    alignContent: "start",
    maxWidth: "34rem",
    margin: "0 auto",
    width: "100%",
  },
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em" },
  panel: {
    display: "grid",
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    minWidth: 0,
    [PHONE]: { padding: tokens.spacingHorizontalM },
  },
  // A button rather than a styled div, so it is reachable by keyboard and announced as an action.
  photoBox: {
    display: "grid",
    placeItems: "center",
    gap: tokens.spacingVerticalS,
    width: "100%",
    minHeight: "11rem",
    padding: tokens.spacingHorizontalM,
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    cursor: "pointer",
    textAlign: "center",
  },
  preview: { maxWidth: "100%", maxHeight: "16rem", borderRadius: tokens.borderRadiusMedium },
  photoIcon: { fontSize: "28px", lineHeight: 1, color: tokens.colorNeutralForeground3 },
  // Square cells, three across a phone. A garment gallery has to read as pictures rather than as
  // a list of file names.
  thumbGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(6rem, 1fr))",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  thumbCell: {
    position: "relative",
    aspectRatio: "1",
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  thumbImage: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  thumbRemove: {
    position: "absolute",
    top: "2px",
    right: "2px",
    minWidth: "unset",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  mainBadge: { position: "absolute", bottom: "3px", left: "3px" },
  thumbAdd: {
    display: "grid",
    placeItems: "center",
    gap: "2px",
    aspectRatio: "1",
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    fontSize: "11px",
    cursor: "pointer",
    ":hover": { border: `2px dashed ${tokens.colorCompoundBrandStroke}` },
  },
  hidden: { display: "none" },
  // Two across where there is room, stacked where there is not. A price box squeezed to 5rem is
  // one people mistype.
  pair: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(9rem, 1fr))",
    gap: tokens.spacingHorizontalM,
  },
  skuList: { display: "grid", gap: "2px", marginTop: tokens.spacingVerticalXS },
  sku: { fontFamily: baeTypography.fontFamilyMonospace, fontSize: "12px" },
  muted: { color: tokens.colorNeutralForeground3 },
  // Sits directly above the button it explains, so the two are read together.
  missing: { color: tokens.colorNeutralForeground3, textAlign: "center" },
  // Big and reachable with a thumb.
  save: { height: "3rem" },
});

/**
 * Add a piece.
 *
 * Built for a phone in a shop: photograph the garment, name it, say what it cost and how many,
 * pick the details from lists, save. One screen, one column, one button.
 *
 * The middle section is generated entirely from what the tenant defined in settings. Nothing about
 * cloth material or art work is written here — this file would render an owner's new "Neckline"
 * field without being touched.
 */
export function AddPiecePage() {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [values, setValues] = useState<Record<string, AttributeValue>>({});
  const [hasVariants, setHasVariants] = useState(false);
  const [variants, setVariants] = useState<VariantChoice>(emptyVariantChoice);
  const [photos, setPhotos] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [saved, setSaved] = useState<{
    sku: string;
    units: ReceivedUnit[];
    variants: { sku: string; optionSummary: string; quantity: number }[];
  } | null>(null);
  const [showLabels, setShowLabels] = useState(false);
  const [photoWarning, setPhotoWarning] = useState<string | null>(null);

  const categories = useQuery({ queryKey: ["categories"], queryFn: commerce.categories });
  const locations = useQuery({ queryKey: ["locations"], queryFn: commerce.locations });

  // Refetched per category, because which fields apply depends on it.
  const attributes = useQuery({
    queryKey: ["attributes", categoryId],
    queryFn: () => catalogue.attributes(categoryId || undefined),
    enabled: categoryId !== "",
  });

  const sites = useMemo(
    () => (locations.data ?? []).filter((l) => l.isSite && l.isActive),
    [locations.data],
  );

  // Hidden categories are not offered, and the full path is shown: with sub-categories in play,
  // two branches can both end in "Long" and the name alone stops being an answer.
  const selectableCategories = useMemo(
    () => (categories.data ?? []).filter((c) => c.isActive),
    [categories.data],
  );

  const categoryLabel = (id: string) =>
    selectableCategories.find((c) => c.id === id)?.path.join(" / ") ?? "";

  const activeAttributes = useMemo(
    () =>
      (attributes.data ?? []).filter((a) => a.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [attributes.data],
  );

  // Fields the owner marked as making SKUs. While variants are on they are answered per SKU in the
  // picker; while they are off they are ordinary questions like any other.
  //
  // Capped at three, in the owner's own order. Three is the platform limit — Shopify caps there
  // too, so a fourth would build SKUs that could never be published — and offering a fourth here
  // only to have the save refused would waste the time of whoever ticked it.
  const axes = useMemo(
    () =>
      activeAttributes.filter((definition) => definition.isVariantAxis).slice(0, MAX_VARIANT_AXES),
    [activeAttributes],
  );

  // Anything past the cap stays an ordinary question, so a fourth axis field is still answered
  // rather than silently dropped off the form.
  const questions = useMemo(
    () =>
      hasVariants
        ? activeAttributes.filter((a) => !axes.some((axis) => axis.id === a.id))
        : activeAttributes,
    [activeAttributes, axes, hasVariants],
  );

  const splitting = hasVariants && axes.length > 0;

  // The same call the picker is showing, so what gets saved is the plan on the screen.
  const plan = useVariantPlan(categoryId, axes, variants.selections, splitting);

  const plannedTotal = plan.combinations.reduce(
    (running, row) => running + (Number(variants.quantities[row.skuSuffix]) || 0),
    0,
  );

  const totalQuantity = splitting ? plannedTotal : Number(quantity);

  const valueFor = (id: string): AttributeValue =>
    values[id] ?? { value: null, selectedValues: [] };

  const save = useMutation({
    mutationFn: async () => {
      const result = await catalogue.quickAdd({
        name: name.trim(),
        description: description.trim() || null,
        categoryId,
        purchasePrice: Number(purchasePrice),
        sellingPrice: sellingPrice ? Number(sellingPrice) : null,
        quantity: splitting ? 0 : Number(quantity),
        siteId: totalQuantity > 0 ? siteId : null,
        storageLocationId: null,
        attributes: questions
          .map((definition) => ({
            attributeDefinitionId: definition.id,
            value: valueFor(definition.id).value,
            selectedValues: valueFor(definition.id).selectedValues,
          }))
          .filter((a) => a.value !== null || (a.selectedValues?.length ?? 0) > 0),
        sku: null,
        axes: splitting ? plan.axes : null,
        variantQuantities: splitting ? variantQuantitiesFrom(variants, plan.combinations) : null,
      });

      // The photos go up after the product exists, because they need something to attach to. A
      // failure here must not lose the piece that was just saved, so it is reported separately.
      if (photos.length > 0) {
        try {
          await catalogue.uploadPhotos(result.productId, photos);
        } catch (caught) {
          setPhotoWarning(
            caught instanceof Error
              ? `${caught.message} The piece was saved — add the photos again from Stock.`
              : "The photos did not upload. The piece was saved.",
          );
        }
      }

      return result;
    },
    onSuccess: (result) => {
      setSaved({ sku: result.sku, units: result.units, variants: result.variants });
      setName("");
      setDescription("");
      setPurchasePrice("");
      setSellingPrice("");
      setQuantity("1");
      setValues({});
      setHasVariants(false);
      setVariants(emptyVariantChoice);
      clearPhotos();
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      void queryClient.invalidateQueries({ queryKey: ["products"] });
    },
  });

  // Object URLs are revoked as they are dropped rather than on unmount: someone photographing a
  // rail of forty garments would otherwise accumulate forty megabytes of blobs in a tab that is
  // never reloaded.
  const addPhotos = (files: File[]) => {
    if (files.length === 0) return;

    setPhotoWarning(null);
    setPhotos((current) => [...current, ...files].slice(0, MAX_PHOTOS));
    setPreviewUrls((current) =>
      [...current, ...files.map((file) => URL.createObjectURL(file))].slice(0, MAX_PHOTOS),
    );
  };

  const removePhoto = (index: number) => {
    setPreviewUrls((current) => {
      const url = current[index];
      if (url) URL.revokeObjectURL(url);
      return current.filter((_, i) => i !== index);
    });
    setPhotos((current) => current.filter((_, i) => i !== index));
  };

  const clearPhotos = () => {
    for (const url of previewUrls) {
      URL.revokeObjectURL(url);
    }
    setPreviewUrls([]);
    setPhotos([]);
  };

  const missingRequired = questions.filter((definition) => {
    if (!definition.isRequired) return false;
    const value = valueFor(definition.id);
    return !value.value && value.selectedValues.length === 0;
  });

  // Named, in the order they appear on the form, so the list reads as directions rather than as a
  // complaint. A disabled button that will not say what it is waiting for is worse than no button:
  // the required tenant fields only appear once a category is chosen, so it is entirely reasonable
  // to have filled in everything visible and still be stuck.
  const missing = [
    name.trim() === "" && "a name",
    categoryId === "" && "a category",
    ...missingRequired.map((definition) => definition.name.toLowerCase()),
    // An axis with nothing ticked would silently drop that axis from every SKU, so it is named
    // the same way a missing required field is.
    ...(splitting
      ? axes
          .filter((axis) => (variants.selections[axis.id] ?? []).length === 0)
          .map((axis) => `at least one ${axis.name.toLowerCase()}`)
      : []),
    purchasePrice === "" && "the purchase price",
    totalQuantity > 0 && siteId === "" && "a store",
  ].filter((entry): entry is string => typeof entry === "string");

  const canSave =
    missing.length === 0 &&
    Number(purchasePrice) >= 0 &&
    // Saving while the plan is still being worked out would send an empty variant list and quietly
    // create one SKU instead of thirty.
    !(splitting && (plan.isFetching || plan.combinations.length === 0)) &&
    !save.isPending;

  if (showLabels && saved) {
    return <LabelSheet units={saved.units} onDone={() => setShowLabels(false)} />;
  }

  return (
    <div className={styles.page}>
      <Subtitle1 className={styles.heading} as="h2">
        Add a piece
      </Subtitle1>

      {save.error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not save</MessageBarTitle>
            {save.error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      {photoWarning && (
        <MessageBar intent="warning">
          <MessageBarBody>{photoWarning}</MessageBarBody>
        </MessageBar>
      )}

      {saved && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>
              {saved.variants.length > 1
                ? `Saved as ${saved.variants.length} SKUs`
                : `Saved as ${saved.sku}`}
            </MessageBarTitle>

            {saved.variants.length > 1 && (
              <div className={styles.skuList}>
                {saved.variants.map((variant) => (
                  <Caption1 key={variant.sku} className={styles.sku}>
                    {variant.sku} — {variant.optionSummary} × {variant.quantity}
                  </Caption1>
                ))}
              </div>
            )}

            {saved.units.length > 0 ? (
              <>
                {/* Worded so it cannot be misread as part of the SKU, which ends in digits. */}
                Ready to print {saved.units.length} labels.{" "}
                <Button appearance="transparent" onClick={() => setShowLabels(true)}>
                  Print them
                </Button>
              </>
            ) : (
              "No labels — this category is not serialised."
            )}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.panel}>
        {/* capture="environment" opens the rear camera straight away on a phone, rather than a
            file browser. On a desktop it falls back to picking a file. */}
        {/* capture="environment" opens the rear camera straight away on a phone rather than a
            file browser, and `multiple` lets the gallery picker take several at once. Both are
            set: a phone honours capture, a desktop falls back to picking files. */}
        <input
          ref={fileInput}
          className={styles.hidden}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          capture="environment"
          multiple
          onChange={(event) => {
            addPhotos(Array.from(event.target.files ?? []));
            // Cleared so photographing the same thing twice still fires a change.
            event.target.value = "";
          }}
        />

        {previewUrls.length === 0 ? (
          <button
            type="button"
            className={styles.photoBox}
            onClick={() => fileInput.current?.click()}
          >
            <span className={styles.photoIcon}>
              <CameraRegular />
            </span>
            <Body1Strong>Take photos</Body1Strong>
            <Caption1 className={styles.muted}>
              The front, the neckline, the work up close — up to {MAX_PHOTOS}
            </Caption1>
          </button>
        ) : (
          <>
            <div className={styles.thumbGrid}>
              {previewUrls.map((url, index) => (
                <div key={url} className={styles.thumbCell}>
                  <img
                    src={url}
                    alt={`The piece, shot ${index + 1}`}
                    className={styles.thumbImage}
                  />

                  {index === 0 && (
                    <Badge
                      className={styles.mainBadge}
                      appearance="filled"
                      color="brand"
                      size="small"
                    >
                      Main
                    </Badge>
                  )}

                  <Button
                    className={styles.thumbRemove}
                    size="small"
                    appearance="subtle"
                    icon={<DismissRegular />}
                    aria-label={`Remove photo ${index + 1}`}
                    onClick={() => removePhoto(index)}
                  />
                </div>
              ))}

              {previewUrls.length < MAX_PHOTOS && (
                <button
                  type="button"
                  className={styles.thumbAdd}
                  onClick={() => fileInput.current?.click()}
                >
                  <span className={styles.photoIcon}>
                    <ImageAddRegular />
                  </span>
                  Add more
                </button>
              )}
            </div>

            <Caption1 className={styles.muted}>
              The first one is what shows in lists. Remove it to promote the next.
            </Caption1>
          </>
        )}
      </div>

      <div className={styles.panel}>
        <Field label="What is it?" required>
          <Input
            value={name}
            placeholder="Indigo Chikan kurti"
            onChange={(_, d) => setName(d.value)}
          />
        </Field>

        <Field label="Category" required>
          <Dropdown
            placeholder={categories.isLoading ? "Loading…" : "Choose a category"}
            value={categoryLabel(categoryId)}
            selectedOptions={categoryId ? [categoryId] : []}
            onOptionSelect={(_, data) => {
              setCategoryId(data.optionValue ?? "");
              // The questions belong to the category, so last category's answers must not carry
              // over to a category that never asked them.
              setValues({});
            }}
          >
            {selectableCategories.map((category) => (
              <Option key={category.id} value={category.id} text={category.path.join(" / ")}>
                {category.path.join(" / ")}
              </Option>
            ))}
          </Dropdown>
        </Field>

        <Field label="Description" hint="Optional.">
          <Textarea value={description} rows={2} onChange={(_, d) => setDescription(d.value)} />
        </Field>
      </div>

      {categoryId !== "" && (
        <div className={styles.panel}>
          <Body1Strong>Details</Body1Strong>

          {attributes.isLoading ? (
            <Spinner size="tiny" label="Loading fields" />
          ) : questions.length === 0 ? (
            <Caption1 className={styles.muted}>
              {axes.length > 0
                ? "Everything about this category is answered per SKU below."
                : "No extra fields for this category. The owner can add them in Settings."}
            </Caption1>
          ) : (
            questions.map((definition) => (
              <AttributeField
                key={definition.id}
                definition={definition}
                value={valueFor(definition.id)}
                onChange={(next) => setValues((current) => ({ ...current, [definition.id]: next }))}
              />
            ))
          )}
        </div>
      )}

      {categoryId !== "" && axes.length > 0 && (
        <VariantPicker
          categoryId={categoryId}
          axes={axes}
          enabled={hasVariants}
          value={variants}
          onToggle={setHasVariants}
          onChange={setVariants}
        />
      )}

      <div className={styles.panel}>
        <div className={styles.pair}>
          <Field label="Purchase price" required hint="What you paid, per piece.">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              value={purchasePrice}
              onChange={(_, d) => setPurchasePrice(d.value)}
            />
          </Field>

          <Field label="Selling price" hint="Optional.">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              value={sellingPrice}
              onChange={(_, d) => setSellingPrice(d.value)}
            />
          </Field>
        </div>

        <div className={styles.pair}>
          {splitting ? (
            <Field label="How many?" hint="Counted per SKU above.">
              <Input readOnly value={`${plannedTotal} in total`} />
            </Field>
          ) : (
            <Field label="How many?" required>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                value={quantity}
                onChange={(_, d) => setQuantity(d.value)}
              />
            </Field>
          )}

          <Field label="Where?" required={totalQuantity > 0}>
            <Dropdown
              placeholder="Choose a store"
              value={sites.find((s) => s.id === siteId)?.name ?? ""}
              selectedOptions={siteId ? [siteId] : []}
              onOptionSelect={(_, data) => setSiteId(data.optionValue ?? "")}
            >
              {sites.map((site) => (
                <Option key={site.id} value={site.id} text={site.name}>
                  {site.name}
                </Option>
              ))}
            </Dropdown>
          </Field>
        </div>
      </div>

      {missing.length > 0 && (
        <Caption1 className={styles.missing}>Still needed: {missing.join(", ")}.</Caption1>
      )}

      <Button
        appearance="primary"
        size="large"
        className={styles.save}
        disabled={!canSave}
        onClick={() => save.mutate()}
      >
        {save.isPending ? <Spinner size="tiny" /> : "Save piece"}
      </Button>
    </div>
  );
}
